import * as _ from 'lodash';
import type { QueryKey } from '@tanstack/react-query';

import type { GetSnippetsParams, SnippetsQueryParams, SnippetStatus } from '../../../../shared/snippets';
import type { RevisionRouteParams } from '../../../components/projects/projectDashboardTypes';

/**
 * Extra information required when comparing snippets between two revisions.
 */
export type CompareSnippetsParams = {
  olderRevisionLocator: string;
  status: SnippetStatus;
};

/**
 * Returns the base part of the snippets query key that is shared across all snippet queries.
 * This is used to find all cached snippet data across different pages/parameters when
 * searching for a specific snippet by ID.
 *
 * @param locator - The revision locator
 * @returns Base query key: ['revisions', locator, 'snippets']
 */
export const getSnippetsBaseQueryKey = (locator: string): QueryKey => {
  return ['revisions', locator, 'snippets'];
};

/**
 * Returns the query key for a specific snippet by its ID.
 *
 * @param locator - The revision locator
 * @param snippetId - The ID of the snippet
 * @returns Query key: ['revisions', locator, 'snippets', snippetId]
 */
export const getSnippetByIdQueryKey = (locator: string, snippetId: string): QueryKey => {
  return [...getSnippetsBaseQueryKey(locator), snippetId];
};

/**
 * Omits client-only snippet parameters from the query parameters.
 *
 * @param params - The original query parameters
 * @returns The modified query parameters without client-only fields
 */
export const omitClientOnlySnippetParams = (params: SnippetsQueryParams): GetSnippetsParams => {
  return _.omit(params, 'selectedSnippetId', 'olderRevisionLocator', 'grouping');
};

/**
 * Route parameters for the snippets feature.
 */
export type SnippetsRouteParams = RevisionRouteParams & {
  status?: SnippetStatus;
};
